import { Divider, Group, Progress, ScrollArea, Stack } from '@mantine/core';
import type { Outcome } from '../../../../../api/strategy-client/strategy-client.type';
import { useTranslation } from 'react-i18next';
import { formatMonthDate } from '../../../../../helpers/date-time-helpers';
import { baseWhite, inkBase, primaryBase, skyDark } from '../../../../../styles/design-tokens';
import { Button } from '../../../../../ui-library/button/button';
import { Icon } from '../../../../../ui-library/icon/icon';
import { Text } from '../../../../../ui-library/typography/typography';
import { SideSectionInfoItem } from '../../../initiative-list/side-section/side-section-info-item';
import { getLastMonthTaskCount, getOutcomeInfo } from './side-section-outcomes.helpers';

type Props = {
  outcome: Outcome | null;
  selectedDate?: Date | null;
  onClickRelatedTasks: () => void;
  onClose: () => void;
};

export const SideSectionOutcomes = ({ outcome, selectedDate, onClickRelatedTasks, onClose }: Props) => {
  const i18n = useTranslation();
  if (!outcome) {
    return null;
  }

  const getCompletedTasksCount = () => {
    return getLastMonthTaskCount(outcome.completed_subtasks_over_time?.tasks);
  };

  const getCommittedTasksCount = () => {
    return getLastMonthTaskCount(outcome.total_subtasks_over_time?.tasks);
  };

  return (
    <Stack h="100%">
      <Group justify="space-between">
        <Text weight="bold" size="medium">
          {`${i18n.t('strategy.tabs.outcomes')} Details`}
        </Text>
        <Icon name="close" onClick={onClose} size={20} color={skyDark} aria-label="Close panel" />
      </Group>
      <Divider />
      <Text size="regular" weight="bold" color={inkBase}>
        {outcome.name}
      </Text>
      <Divider />
      <ScrollArea h="100%" scrollbarSize={0}>
        <Stack gap={8}>
          {selectedDate && (
            <SideSectionInfoItem
              label={i18n.t('strategy.initiative_performance.selected_date', 'Selected Date')}
              value={formatMonthDate(selectedDate)}
            />
          )}
          {getOutcomeInfo(outcome, i18n.t).map((info) => (
            <SideSectionInfoItem key={info.label} label={info.label} value={info.value} />
          ))}
          <SideSectionInfoItem
            label={`Completed ${i18n.t('common.work_units')}`}
            value={`${getCompletedTasksCount()} / ${getCommittedTasksCount()}`}
          />
          <Progress value={outcome.percent_progress} color={primaryBase} size={4} />
        </Stack>
      </ScrollArea>
      <Button
        variant="secondary"
        size="xs"
        radius="xl"
        fullWidth
        style={{ flexShrink: 0 }}
        leftSection={<Icon name="remove_red_eye" variant="filled" color={baseWhite} size={14} pointer />}
        onClick={onClickRelatedTasks}
      >
        <Text size="small" color={baseWhite}>
          {i18n.t('strategy.initiative_performance.related_tasks.title', 'Related Work Units')}
        </Text>
      </Button>
    </Stack>
  );
};
